import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Layout from '@/components/Layout';
import SmartHeader from '@/components/SmartHeader';
import { useAuth } from '@/contexts/AuthContext';
import { BookCheck, ClipboardCheck, Heart, FileText, FileDown, X, Newspaper, ShoppingBag, Award, Clock, BookOpen, Moon, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { resolveImgUrl } from '@/utils/imgUrl';
import { KELAS_CONFIG } from './InputSetoran';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const MONTHS_ID_REKAP = ['Januari','Februari','Maret','April','Mei','Juni','Juli','Agustus','September','Oktober','November','Desember'];

// Convert pages to juz. Juz 30 = 23 halaman, all others = 20 halaman
const pagesToJuz = (juzNum, pages) => {
  const pph = Number(juzNum) === 30 ? 23 : 20;
  return (Number(pages) || 0) / pph;
};

const formatJuz = (juz) => {
  if (!juz || juz <= 0) return '0 Juz';
  const rounded = Math.round(juz * 10) / 10;
  return `${rounded} Juz`;
};

export default function GuruDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [santriList, setSantriList] = useState([]);
  const [showRekapModal, setShowRekapModal] = useState(false);
  const [loadingRekap, setLoadingRekap] = useState(false);
  const [isExportingRekap, setIsExportingRekap] = useState(false);
  const [rekapData, setRekapData] = useState([]);
  const [settings, setSettings] = useState({});
  const [guruFoto, setGuruFoto] = useState('');

  // Raw data for rekap
  const [rekapAgg, setRekapAgg] = useState([]);
  const [rawAbsensiRekap, setRawAbsensiRekap] = useState([]);

  // Selected month for rekap (YYYY-MM)
  const [rekapBulan, setRekapBulan] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/santri`).then(r => setSantriList(r.data)).catch(() => {}),
      axios.get(`${API}/settings`).then(r => setSettings(r.data)).catch(() => {}),
      axios.get(`${API}/guru`).then(r => {
        const mine = r.data.find(g => g.name === user?.name);
        if (mine?.foto) setGuruFoto(mine.foto);
      }).catch(() => {}),
    ]);
  }, [user]);

  // Recompute rekapData whenever aggregated data or selected month changes
  useEffect(() => {
    if (rekapAgg.length === 0 && rawAbsensiRekap.length === 0) return;
    const [yr, mo] = rekapBulan.split('-').map(Number);

    const data = santriList.map(s => {
      const agg = rekapAgg.find(a => a.santri_id === s.id) || {};

      // Absensi — bulan terpilih, deduplicated by tanggal+sesi
      const absensi = { H: 0, I: 0, S: 0, A: 0, TS: 0 };
      const slotMap = new Map();
      rawAbsensiRekap
        .filter(a => a.santri_id === s.id)
        .forEach(a => {
          const [ay, am] = (a.tanggal || '').split('-').map(Number);
          if (ay === yr && am === mo) {
            const sesi = a.sesi === 'malam' ? 'malam' : 'sore';
            slotMap.set(`${a.tanggal}:${sesi}`, a.status);
          }
        });
      for (const status of slotMap.values()) {
        if (absensi[status] !== undefined) absensi[status]++;
      }

      return {
        santri: s,
        absensi,
        totalZiyadahJuz:    agg.total_ziyadah_juz    || 0,
        totalBinnadzarJuz:  agg.total_binnadzar_juz  || 0,
        totalZiyadahPages:  agg.total_ziyadah_pages  || 0,
        totalBinnadzarPages:agg.total_binnadzar_pages|| 0,
        monthZiyadahPages:  agg.month_ziyadah_pages  || 0,
      };
    });

    setRekapData(data);
  }, [rekapAgg, rawAbsensiRekap, rekapBulan, santriList]);

  // Re-fetch aggregation when month changes while modal is open
  useEffect(() => {
    if (!showRekapModal) return;
    setLoadingRekap(true);
    axios.get(`${API}/setoran/rekap?bulan=${rekapBulan}`, { withCredentials: true })
      .then(r => setRekapAgg(r.data))
      .catch(() => {})
      .finally(() => setLoadingRekap(false));
  }, [rekapBulan]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpenRekap = async () => {
    setShowRekapModal(true);
    setLoadingRekap(true);
    try {
      const [aggData, absensiData] = await Promise.all([
        axios.get(`${API}/setoran/rekap?bulan=${rekapBulan}`, { withCredentials: true }).then(r => r.data),
        axios.get(`${API}/absensi`, { withCredentials: true }).then(r => r.data),
      ]);
      setRekapAgg(aggData);
      setRawAbsensiRekap(absensiData);
    } catch { toast.error('Gagal memuat data rekap'); }
    finally { setLoadingRekap(false); }
  };

  const handleLogout = async () => { await logout(); navigate('/welcome'); };

  const exportRekapPDF = async () => {
    if (rekapData.length === 0) { toast.error('Tidak ada data rekap'); return; }
    setIsExportingRekap(true);
    try {
      const [yr, mo] = rekapBulan.split('-').map(Number);
      const periodeStr = `${MONTHS_ID_REKAP[mo - 1]} ${yr}`;
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const W = 297;

      // Header bar
      doc.setFillColor(16, 185, 129);
      doc.rect(0, 0, W, 18, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11); doc.setFont('helvetica', 'bold');
      doc.text('REKAP PERKEMBANGAN SANTRI', 8, 8);
      doc.setFontSize(8); doc.setFont('helvetica', 'normal');
      doc.text(`Periode: ${periodeStr}  ·  ${rekapData.length} Santri`, 8, 14);
      doc.setTextColor(0, 0, 0);

      const tableData = rekapData.map((item, i) => [
        i + 1,
        item.santri.name || '-',
        item.santri.kelas || '-',
        item.absensi.H || 0,
        item.absensi.S || 0,
        item.absensi.I || 0,
        item.absensi.A || 0,
        item.absensi.TS || 0,
        formatJuz(item.totalZiyadahJuz),
        formatJuz(item.totalBinnadzarJuz),
        item.monthZiyadahPages > 0 ? `${item.monthZiyadahPages} hal` : '-',
      ]);

      autoTable(doc, {
        head: [['No', 'Nama Santri', 'Kelas', 'H', 'S', 'I', 'A', 'TS', 'Ziyadah', 'Bin Nadzar', 'Progres Bln (hal)']],
        body: tableData,
        startY: 22,
        margin: { left: 6, right: 6 },
        styles: { fontSize: 8, cellPadding: 2.5, halign: 'center' },
        headStyles: { fillColor: [4, 120, 87], textColor: 255, fontStyle: 'bold' },
        columnStyles: {
          0: { cellWidth: 8 },
          1: { halign: 'left', cellWidth: 58 },
          2: { cellWidth: 14 },
        },
        alternateRowStyles: { fillColor: [240, 253, 244] },
        didParseCell: (data) => {
          if (data.section === 'head') return;
          if (data.column.index === 3) { data.cell.styles.textColor = [5, 150, 105]; data.cell.styles.fontStyle = 'bold'; }
          if (data.column.index === 4) { data.cell.styles.textColor = [37, 99, 235]; data.cell.styles.fontStyle = 'bold'; }
          if (data.column.index === 5) { data.cell.styles.textColor = [217, 119, 6]; data.cell.styles.fontStyle = 'bold'; }
          if (data.column.index === 6) { data.cell.styles.textColor = [220, 38, 38]; data.cell.styles.fontStyle = 'bold'; }
          if (data.column.index === 7) { data.cell.styles.textColor = [75, 85, 99]; data.cell.styles.fontStyle = 'bold'; }
        },
      });

      const legY = doc.internal.pageSize.getHeight() - 8;
      doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(107, 114, 128);
      doc.text('Keterangan: H=Hadir  S=Sakit  I=Izin  A=Alfa  TS=Tidak Setor', 8, legY);
      doc.text(`Dicetak: ${new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}`, W - 8, legY, { align: 'right' });

      doc.save(`Rekap-Perkembangan-${periodeStr.replace(' ', '-')}.pdf`);
      toast.success('PDF berhasil diunduh');
    } catch (e) {
      console.error(e);
      toast.error('Gagal ekspor PDF');
    } finally { setIsExportingRekap(false); }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        <SmartHeader foto={guruFoto} onLogout={handleLogout} />

        <div className="px-5 py-5 space-y-5 page-enter">

          {/* ═══ AKSI CEPAT ═══ */}
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">Aksi Cepat</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { icon: BookCheck,     label: 'Input Setoran',  sub: 'Catat hafalan santri',   path: '/guru/setoran',        color: 'emerald' },
                { icon: ClipboardCheck,label: 'Absensi Manual', sub: 'Input kehadiran santri',  path: '/guru/setoran?tab=absensi', color: 'blue'    },
                { icon: FileText,      label: 'Rekap Perkembangan', sub: 'Tabel rekap hafalan santri', color: 'amber',         action: () => handleOpenRekap() },
                { icon: Award,         label: "Input Tasmi'",   sub: 'Catat ujian tasmi santri', path: '/guru/tasmi',          color: 'teal'    },
              ].map(({ icon: Icon, label, sub, path, color, action }) => (
                <button key={label} data-testid={`aksi-${label.replace(/\s+/g, '-').toLowerCase()}-btn`}
                  onClick={() => action ? action() : navigate(path)}
                  className={`bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex items-center gap-3 active:scale-95 transition-all text-left hover:bg-gray-50`}>
                  <div className={`w-11 h-11 rounded-xl bg-${color}-100 flex items-center justify-center flex-shrink-0`}>
                    <Icon size={20} className={`text-${color}-600`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-800 leading-tight">{label}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5 leading-tight">{sub}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* ═══ FITUR LAINNYA ═══ */}
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">Fitur Lainnya</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {[
                { icon: BookOpen,    label: 'Al-Quran',      sub: 'Baca Al-Quran digital',      path: '/quran',   color: 'emerald' },
                { icon: Moon,        label: 'Tasbih Digital', sub: 'Zikir & tasbih harian',      path: '/tasbih',  color: 'amber'  },
                { icon: Newspaper,   label: 'BQ News',       sub: 'Berita & informasi',         path: '/berita',  color: 'blue'   },
                { icon: ShoppingBag, label: 'Toko',          sub: 'Produk pesantren',           path: '/toko',    color: 'orange' },
                { icon: Heart,       label: 'Donasi',        sub: 'Donasi & infaq pesantren',   path: '/donasi',  color: 'red'    },
              ].map(({ icon: Icon, label, sub, path, color }, idx, arr) => (
                <button key={label}
                  onClick={() => navigate(path)}
                  className={`bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex items-center gap-3 active:scale-95 transition-all text-left hover:bg-gray-50 ${arr.length % 2 !== 0 && idx === arr.length - 1 ? 'col-span-2 md:col-span-1' : ''}`}>
                  <div className={`w-11 h-11 rounded-xl bg-${color}-100 flex items-center justify-center flex-shrink-0`}>
                    <Icon size={20} className={`text-${color}-600`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-800 leading-tight">{label}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5 leading-tight">{sub}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

        </div>

        {/* Rekap Perkembangan Modal — Fullscreen */}
        <AnimatePresence>
          {showRekapModal && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9999] bg-gray-50 flex flex-col"
              data-testid="rekap-perkembangan-modal">

              {/* Header */}
              <div className="flex-shrink-0 bg-emerald-700 px-4 pt-12 pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <img src="/logo-zahbaq.png" alt="ZahBaq" className="w-8 h-8 object-contain rounded-lg bg-white/10 p-0.5" />
                    <div>
                      <p className="font-bold text-white text-sm leading-tight">Rekap Perkembangan</p>
                      <p className="text-[10px] text-emerald-200 leading-tight">Hafalan Santri Halaqah</p>
                    </div>
                  </div>
                  <button onClick={() => setShowRekapModal(false)} className="p-2 bg-white/15 rounded-xl text-white">
                    <X size={18} />
                  </button>
                </div>
              </div>

              {/* Sub-header dengan month picker + export */}
              <div className="flex-shrink-0 bg-emerald-50 border-b border-emerald-100 py-2 px-4 flex items-center justify-between gap-3">
                <p className="text-[10px] text-emerald-600 flex-shrink-0">{santriList.length} santri</p>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider flex-shrink-0">Bulan:</span>
                  <input
                    data-testid="rekap-bulan-picker"
                    type="month"
                    value={rekapBulan}
                    onChange={e => setRekapBulan(e.target.value)}
                    className="text-xs text-emerald-800 font-semibold bg-white border border-emerald-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                  <button
                    data-testid="rekap-export-pdf-btn"
                    onClick={exportRekapPDF}
                    disabled={isExportingRekap || loadingRekap || rekapData.length === 0}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-xs font-semibold shadow-sm transition-all disabled:opacity-50 flex-shrink-0"
                  >
                    {isExportingRekap
                      ? <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      : <><FileDown size={13} /> Export PDF</>}
                  </button>
                </div>
              </div>

              {/* Content */}
              {loadingRekap ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="w-6 h-6 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <div className="flex-1 overflow-y-auto">
                  {rekapData.length === 0 ? (
                    <p className="text-center text-gray-400 text-sm py-12">Belum ada data santri</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="border-collapse" style={{ minWidth: 620, width: '100%' }}>
                        <thead>
                          <tr className="bg-emerald-700 text-white">
                            <th rowSpan={2} className="px-3 py-2 text-[9px] font-bold text-left border-r border-emerald-600 whitespace-nowrap">Nama Santri</th>
                            <th rowSpan={2} className="px-3 py-2 text-[9px] font-bold text-center border-r border-emerald-600 whitespace-nowrap">Grade</th>
                            <th colSpan={5} className="px-3 py-1.5 text-[9px] font-bold text-center border-r border-emerald-600">Absensi</th>
                            <th rowSpan={2} className="px-3 py-2 text-[9px] font-bold text-center border-r border-emerald-600 whitespace-nowrap">Ziyadah (Juz)</th>
                            <th rowSpan={2} className="px-3 py-2 text-[9px] font-bold text-center border-r border-emerald-600 whitespace-nowrap">Binnadzar (Juz)</th>
                            <th rowSpan={2} className="px-3 py-2 text-[9px] font-bold text-center whitespace-nowrap">Progres Bln (hal)</th>
                          </tr>
                          <tr className="bg-emerald-800 text-white">
                            {['H','I','S','A','TS'].map(k => (
                              <th key={k} className="px-2 py-1.5 text-[9px] font-bold text-center border-r border-emerald-700 last:border-r-emerald-600">{k}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {rekapData.map((item, i) => (
                            <tr key={item.santri.id} data-testid={`rekap-row-${item.santri.id}`}
                              className={i % 2 === 0 ? 'bg-white' : 'bg-emerald-50/30'}>
                              <td className="px-3 py-2.5 border-r border-gray-100">
                                <div className="flex items-center gap-2 whitespace-nowrap">
                                  <div className="w-6 h-6 rounded-md bg-emerald-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                                    {item.santri.foto
                                      ? <img src={resolveImgUrl(item.santri.foto)} alt={item.santri.name} className="w-full h-full object-cover" />
                                      : <span className="text-emerald-700 font-bold text-[9px]">{item.santri.name?.charAt(0)}</span>}
                                  </div>
                                  <span className="text-[11px] font-semibold text-gray-800">{item.santri.name}</span>
                                </div>
                              </td>
                              <td className="px-3 py-2.5 text-center border-r border-gray-100">
                                {item.santri.kelas ? (
                                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-md ${KELAS_CONFIG[item.santri.kelas]?.color}`}>
                                    {item.santri.kelas}
                                  </span>
                                ) : <span className="text-[10px] text-gray-400">-</span>}
                              </td>
                              {['H','I','S','A','TS'].map(k => (
                                <td key={k} className={`px-2 py-2.5 text-[10px] font-bold text-center border-r border-gray-100 ${
                                  k === 'H' ? 'text-emerald-600' : k === 'A' ? 'text-red-500' : k === 'TS' ? 'text-gray-600' : k === 'S' ? 'text-blue-500' : 'text-amber-500'
                                }`}>
                                  {item.absensi[k] || 0}
                                </td>
                              ))}
                              <td className="px-3 py-2.5 text-[10px] font-semibold text-center text-emerald-700 border-r border-gray-100 whitespace-nowrap">
                                {formatJuz(item.totalZiyadahJuz)}
                              </td>
                              <td className="px-3 py-2.5 text-[10px] font-semibold text-center text-purple-700 border-r border-gray-100 whitespace-nowrap">
                                {formatJuz(item.totalBinnadzarJuz)}
                              </td>
                              <td className="px-3 py-2.5 text-center">
                                <span className="text-[10px] font-bold text-emerald-700">
                                  {item.monthZiyadahPages > 0 ? `${item.monthZiyadahPages} hal` : '-'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Footer */}
              <div className="flex-shrink-0 border-t border-gray-100 bg-white px-4 pt-3"
                style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 12px) + 12px)' }}>
                <button data-testid="rekap-tutup-btn"
                  onClick={() => setShowRekapModal(false)}
                  className="w-full py-3.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm font-semibold transition-all">
                  Tutup
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Layout>
  );
}
